// ─── PSYCH-MODE THEMES  —  Visual Redesign V2 ──────────────────────────────────
// T is a mutable singleton. Object.assign(T, THEMES[persona]) runs at the top
// of every App() render — before any JSX is evaluated. All module-level
// subcomponents close over T by reference, so they see the updated values on
// each re-render without needing props. No re-render loop: Object.assign does
// not call setState. The persona setState triggers the re-render; during that
// render Object.assign fires and the new theme is live for all children.
//
// shadowSm, shadowXs, gold are embedded in each theme so shadows and the
// Intelligence accent colour can shift per mode without extra variables.
//
// T.aiColor / T.aiColorLight — amber tokens for AI-powered features only.
// T.border — for inputs and interactive controls only, NOT card wrappers.
// Cards float via T.shadow alone.

// Shared keys that every theme must define for dark variants to inherit
const _sharedTypo = {
  radius:    "18px",
  radiusSm:  "12px",
  radiusXs:  "8px",
  radiusPill: "24px",
  fontHead:  "'Poppins','Inter',system-ui,sans-serif",
  fontMono:  "'Inter','system-ui',sans-serif",
  fontSans:  "'Inter',system-ui,sans-serif",
  fontDisplay: "'Outfit','Inter',system-ui,sans-serif",
};

// Universal macro colours — fixed across all personas (nutrition meaning, not identity)
const _macros = {
  macroCalories:  "#F5A623",
  macroProtein:   "#E74C3C",
  macroCarbs:     "#3498DB",
  macroFat:       "#9B59B6",
  macroFibre:     "#2ECC71",
  macroSugar:     "#E91E8C",
  macroSodium:    "#95A5A6",
};

export const THEMES = {
  // ── BALANCED — Clinical steel blue ──────────────────────────────────────
  Balanced: {
    ..._sharedTypo,
    ..._macros,
    bg:        "#e4edf4",
    surface:   "#dce6ed",
    card:      "#FFFFFF",
    cardHi:    "#e4f0f7",
    accent:    "#2196b8",
    secondary: "#4a9e2e",
    yellow:    "#f9c74f",
    orange:    "#f97316",
    red:       "#e24b4a",
    ink:       "#1a1a1a",
    text:      "#1a1a1a",
    textSub:   "#4a4a4a",
    textDim:   "#8a8a8a",
    border:    "1px solid #d4e8f2",
    borderColor: "#d4e8f2",
    shadow:    "0 8px 32px rgba(26,107,138,0.18)",
    shadowSm:  "0 4px 16px rgba(26,107,138,0.13)",
    shadowXs:  "0 2px 8px rgba(26,107,138,0.09)",
    gold:      "#2196b8",
    aiColor:   "#b86e00",
    aiColorLight: "#fef3d0",
    // New design-system keys
    primary:       "#1a6b8a",
    primaryDark:   "#0d4a63",
    primaryLight:  "#b8e0ed",
    accentLight:   "#e1f4fb",
    muted:         "#4a7a8a",
    success:       "#4a9e2e",
    warning:       "#e6b800",
    danger:        "#e24b4a",
    // Meal colours
    mealBreakfast:     "#f9c74f",
    mealBreakfastText: "#7a5900",
    mealLunch:         "#1a6b8a",
    mealLunchText:     "#ffffff",
    mealDinner:        "#7c4f9e",
    mealDinnerText:    "#ffffff",
    mealSnack:         "#D4856A",
    mealSnackText:     "#ffffff",
    // Log Food button
    logFoodBg:      "#1a6b8a",
    logFoodText:    "#ffffff",
    logFoodBorder:  "#1a6b8a",
    // Hydration
    hydrationBlue:     "#3b82f6",
    hydrationBlueTint: "rgba(59,130,246,0.15)",
    // Dashboard redesign tokens
    tileBg: "#0f2d3d",
    tileAccent: "#3ecfa0",
    tileMuted: "#5a8fa8",
    tileText: "#ffffff",
    pageBg: "#f2f6f9",
    cardBorder: "#e8eef3",
    surfaceLight: "#f0f6fa",
    surfaceMid: "#e2eaf0",
    inkDark: "#0d2d3a",
    inkMuted: "#94a3b0",
    inkFaint: "#b0c0cc",
    aiTagBg: "#fff5ee",
    aiTagText: "#f97316",
    chipBorder: "#d8e4ec",
    chipText: "#5a7a8a",
    disclaimerText: "#c0cdd6",
    progCardBg: "#0d3d4d",
    progBannerBg: "#0a2f3c",
    progEyebrow: "#3ecfa0",
    liftRed: "#e74c3c",
    nutriGreen: "#2ecc71",
    actionPrimary: "#f97316",
    backdrop: "rgba(0,0,0,0.75)",
  },
  // ── CRITICAL — Stark charcoal/red ───────────────────────────────────────
  Critical: {
    ..._sharedTypo,
    ..._macros,
    bg:        "#1c1c1e",
    surface:   "#2a2a2c",
    card:      "#2a2a2c",
    cardHi:    "#3a3a3c",
    accent:    "#e24b4a",
    secondary: "#e8a020",
    yellow:    "#e8a020",
    orange:    "#a32d2d",
    red:       "#e24b4a",
    ink:       "#ffffff",
    text:      "#f9fafb",
    textSub:   "#d1d5db",
    textDim:   "#888888",
    border:    "1px solid #3a3a3c",
    borderColor: "#3a3a3c",
    shadow:    "0 8px 32px rgba(0,0,0,0.55)",
    shadowSm:  "0 4px 16px rgba(0,0,0,0.45)",
    shadowXs:  "0 1px 4px rgba(0,0,0,0.28)",
    gold:      "#e24b4a",
    aiColor:   "#b86e00",
    aiColorLight: "#fef3d0",
    // New design-system keys
    primary:       "#a32d2d",
    primaryDark:   "#1c1c1e",
    primaryLight:  "#f09595",
    accentLight:   "#fcebeb",
    muted:         "#888888",
    success:       "#4a9e2e",
    warning:       "#e8a020",
    danger:        "#e24b4a",
    // Meal colours
    mealBreakfast:     "#e8a020",
    mealBreakfastText: "#ffffff",
    mealLunch:         "#a32d2d",
    mealLunchText:     "#ffffff",
    mealDinner:        "#444444",
    mealDinnerText:    "#ffffff",
    mealSnack:         "#D4856A",
    mealSnackText:     "#ffffff",
    // Log Food button
    logFoodBg:      "#1c1c1e",
    logFoodText:    "#e24b4a",
    logFoodBorder:  "#e24b4a",
    // Hydration
    hydrationBlue:     "#1d4ed8",
    hydrationBlueTint: "rgba(29,78,216,0.15)",
    // Dashboard redesign tokens
    tileBg: "#2a1a1a",
    tileAccent: "#e24b4a",
    tileMuted: "#aa7a7a",
    tileText: "#ffffff",
    pageBg: "#1c1c1e",
    cardBorder: "#3a2a2a",
    surfaceLight: "#2a2020",
    surfaceMid: "#3a2a2a",
    inkDark: "#ffffff",
    inkMuted: "#aaaaaa",
    inkFaint: "#666666",
    aiTagBg: "#3a1a1a",
    aiTagText: "#e24b4a",
    chipBorder: "#4a2a2a",
    chipText: "#cc8888",
    disclaimerText: "#666666",
    progCardBg: "#2a1a1a",
    progBannerBg: "#1a1010",
    progEyebrow: "#e24b4a",
    liftRed: "#e74c3c",
    nutriGreen: "#2ecc71",
    actionPrimary: "#f97316",
    backdrop: "rgba(0,0,0,0.75)",
  },
  // ── MOTIVATIONAL — Uplifting greens ─────────────────────────────────────
  Motivational: {
    ..._sharedTypo,
    ..._macros,
    bg:        "#e2ede0",
    surface:   "#d8ebd2",
    card:      "#FFFFFF",
    cardHi:    "#dcf0d4",
    accent:    "#7ec850",
    secondary: "#4a9e2e",
    yellow:    "#f9c74f",
    orange:    "#3a8a20",
    red:       "#e07a5f",
    ink:       "#0d1a08",
    text:      "#0d1a08",
    textSub:   "#2a4a1a",
    textDim:   "#6b8f5a",
    border:    "1px solid #cce8c0",
    borderColor: "#cce8c0",
    shadow:    "0 8px 32px rgba(58,138,32,0.18)",
    shadowSm:  "0 4px 16px rgba(58,138,32,0.13)",
    shadowXs:  "0 2px 8px rgba(58,138,32,0.09)",
    gold:      "#7ec850",
    aiColor:   "#b86e00",
    aiColorLight: "#fef3d0",
    // New design-system keys
    primary:       "#3a8a20",
    primaryDark:   "#22591a",
    primaryLight:  "#b8e89a",
    accentLight:   "#d4f0b0",
    muted:         "#6b8f5a",
    success:       "#4a9e2e",
    warning:       "#f9c74f",
    danger:        "#e07a5f",
    // Meal colours
    mealBreakfast:     "#f9c74f",
    mealBreakfastText: "#7a5900",
    mealLunch:         "#4a9e2e",
    mealLunchText:     "#ffffff",
    mealDinner:        "#277da1",
    mealDinnerText:    "#ffffff",
    mealSnack:         "#D4856A",
    mealSnackText:     "#ffffff",
    // Log Food button
    logFoodBg:      "#4a9e2e",
    logFoodText:    "#ffffff",
    logFoodBorder:  "#4a9e2e",
    // Hydration
    hydrationBlue:     "#0ea5e9",
    hydrationBlueTint: "rgba(14,165,233,0.15)",
    // Dashboard redesign tokens
    tileBg: "#0f3020",
    tileAccent: "#4ade80",
    tileMuted: "#5a8f6a",
    tileText: "#ffffff",
    pageBg: "#f0f5f0",
    cardBorder: "#d4ecd4",
    surfaceLight: "#eaf4ea",
    surfaceMid: "#d8ebd2",
    inkDark: "#0d2d12",
    inkMuted: "#6b8f6b",
    inkFaint: "#9ab89a",
    aiTagBg: "#f0fff0",
    aiTagText: "#3a8a20",
    chipBorder: "#c0e0c0",
    chipText: "#3a6a3a",
    disclaimerText: "#9ab89a",
    progCardBg: "#0d3d1a",
    progBannerBg: "#0a2f14",
    progEyebrow: "#4ade80",
    liftRed: "#e74c3c",
    nutriGreen: "#2ecc71",
    actionPrimary: "#f97316",
    backdrop: "rgba(0,0,0,0.75)",
  },
  // ── BALANCED DARK ─────────────────────────────────────────────────────────
  BalancedDark: {
    ..._sharedTypo,
    ..._macros,
    bg:        "#0f1922",
    surface:   "#1a2d38",
    card:      "#1a2d38",
    cardHi:    "#27333f",
    accent:    "#2196b8",
    secondary: "#4a9e2e",
    yellow:    "#f9c74f",
    orange:    "#f97316",
    red:       "#e24b4a",
    ink:       "#f9fafb",
    text:      "#f9fafb",
    textSub:   "#d1d5db",
    textDim:   "#6b7280",
    border:    "1px solid #374151",
    borderColor: "#374151",
    shadow:    "0 4px 20px rgba(0,0,0,0.40)",
    shadowSm:  "0 2px 10px rgba(0,0,0,0.32)",
    shadowXs:  "0 1px 3px rgba(0,0,0,0.2)",
    gold:      "#2196b8",
    aiColor:   "#b86e00",
    aiColorLight: "#fef3d0",
    primary:       "#1a6b8a",
    primaryDark:   "#0d4a63",
    primaryLight:  "#b8e0ed",
    accentLight:   "#1a3a48",
    muted:         "#4a7a8a",
    success:       "#4a9e2e",
    warning:       "#e6b800",
    danger:        "#e24b4a",
    mealBreakfast: "#f9c74f", mealBreakfastText: "#7a5900",
    mealLunch: "#1a6b8a", mealLunchText: "#ffffff",
    mealDinner: "#7c4f9e", mealDinnerText: "#ffffff",
    mealSnack: "#D4856A", mealSnackText: "#ffffff",
    logFoodBg: "#1a6b8a", logFoodText: "#ffffff", logFoodBorder: "#1a6b8a",
    hydrationBlue: "#60a5fa", hydrationBlueTint: "rgba(96,165,250,0.15)",
    // Dashboard redesign tokens (dark-adapted)
    tileBg: "#0f2d3d",
    tileAccent: "#3ecfa0",
    tileMuted: "#5a8fa8",
    tileText: "#ffffff",
    pageBg: "#0f1922",
    cardBorder: "#2a3a48",
    surfaceLight: "#1a2d38",
    surfaceMid: "#27333f",
    inkDark: "#ffffff",
    inkMuted: "#a0b0bc",
    inkFaint: "#6a7a88",
    aiTagBg: "#2a1f14",
    aiTagText: "#f9a356",
    chipBorder: "#3a4a58",
    chipText: "#a0b8c8",
    disclaimerText: "#6a7a88",
    progCardBg: "#0d3d4d",
    progBannerBg: "#0a2f3c",
    progEyebrow: "#3ecfa0",
    liftRed: "#e74c3c",
    nutriGreen: "#2ecc71",
    actionPrimary: "#f97316",
    backdrop: "rgba(0,0,0,0.75)",
  },
  // ── CRITICAL DARK (same as light — already near-black) ────────────────────
  CriticalDark: {
    ..._sharedTypo,
    ..._macros,
    bg:        "#000000",
    surface:   "#141414",
    card:      "#1a1a1a",
    cardHi:    "#222222",
    accent:    "#e24b4a",
    secondary: "#e8a020",
    yellow:    "#e8a020",
    orange:    "#a32d2d",
    red:       "#e24b4a",
    ink:       "#ffffff",
    text:      "#f9fafb",
    textSub:   "#d1d5db",
    textDim:   "#888888",
    border:    "1px solid #2a2a2a",
    borderColor: "#2a2a2a",
    shadow:    "0 2px 12px rgba(0,0,0,0.5)",
    shadowSm:  "0 1px 6px rgba(0,0,0,0.4)",
    shadowXs:  "0 1px 3px rgba(0,0,0,0.3)",
    gold:      "#e24b4a",
    aiColor:   "#b86e00",
    aiColorLight: "#fef3d0",
    primary: "#a32d2d", primaryDark: "#000000", primaryLight: "#f09595",
    accentLight: "#2a1010", muted: "#888888",
    success: "#4a9e2e", warning: "#e8a020", danger: "#e24b4a",
    mealBreakfast: "#e8a020", mealBreakfastText: "#ffffff",
    mealLunch: "#a32d2d", mealLunchText: "#ffffff",
    mealDinner: "#444444", mealDinnerText: "#ffffff",
    mealSnack: "#D4856A", mealSnackText: "#ffffff",
    logFoodBg: "#000000", logFoodText: "#e24b4a", logFoodBorder: "#e24b4a",
    hydrationBlue: "#3b82f6", hydrationBlueTint: "rgba(59,130,246,0.15)",
    // Dashboard redesign tokens (copied from Critical light)
    tileBg: "#2a1a1a",
    tileAccent: "#e24b4a",
    tileMuted: "#aa7a7a",
    tileText: "#ffffff",
    pageBg: "#1c1c1e",
    cardBorder: "#3a2a2a",
    surfaceLight: "#2a2020",
    surfaceMid: "#3a2a2a",
    inkDark: "#ffffff",
    inkMuted: "#aaaaaa",
    inkFaint: "#666666",
    aiTagBg: "#3a1a1a",
    aiTagText: "#e24b4a",
    chipBorder: "#4a2a2a",
    chipText: "#cc8888",
    disclaimerText: "#666666",
    progCardBg: "#2a1a1a",
    progBannerBg: "#1a1010",
    progEyebrow: "#e24b4a",
    liftRed: "#e74c3c",
    nutriGreen: "#2ecc71",
    actionPrimary: "#f97316",
    backdrop: "rgba(0,0,0,0.75)",
  },
  // ── MOTIVATIONAL DARK ─────────────────────────────────────────────────────
  MotivationalDark: {
    ..._sharedTypo,
    ..._macros,
    bg:        "#0c1509",
    surface:   "#172214",
    card:      "#172214",
    cardHi:    "#222e20",
    accent:    "#7ec850",
    secondary: "#4ade80",
    yellow:    "#f9c74f",
    orange:    "#3a8a20",
    red:       "#e07a5f",
    ink:       "#f0fdf4",
    text:      "#f0fdf4",
    textSub:   "#d1d5db",
    textDim:   "#6b8f5a",
    border:    "1px solid #2a3428",
    borderColor: "#2a3428",
    shadow:    "0 4px 20px rgba(0,0,0,0.42)",
    shadowSm:  "0 2px 10px rgba(0,0,0,0.32)",
    shadowXs:  "0 1px 3px rgba(0,0,0,0.2)",
    gold:      "#7ec850",
    aiColor:   "#b86e00",
    aiColorLight: "#fef3d0",
    primary: "#3a8a20", primaryDark: "#0a1a08", primaryLight: "#b8e89a",
    accentLight: "#1a3418", muted: "#6b8f5a",
    success: "#4a9e2e", warning: "#f9c74f", danger: "#e07a5f",
    mealBreakfast: "#f9c74f", mealBreakfastText: "#7a5900",
    mealLunch: "#4a9e2e", mealLunchText: "#ffffff",
    mealDinner: "#277da1", mealDinnerText: "#ffffff",
    mealSnack: "#D4856A", mealSnackText: "#ffffff",
    logFoodBg: "#4a9e2e", logFoodText: "#ffffff", logFoodBorder: "#4a9e2e",
    hydrationBlue: "#38bdf8", hydrationBlueTint: "rgba(56,189,248,0.15)",
    // Dashboard redesign tokens (dark-adapted)
    tileBg: "#0f3020",
    tileAccent: "#4ade80",
    tileMuted: "#5a8f6a",
    tileText: "#ffffff",
    pageBg: "#0c1509",
    cardBorder: "#2a3428",
    surfaceLight: "#172214",
    surfaceMid: "#222e20",
    inkDark: "#ffffff",
    inkMuted: "#a0b8a0",
    inkFaint: "#6a8a6a",
    aiTagBg: "#1a2a14",
    aiTagText: "#7ec850",
    chipBorder: "#3a4a38",
    chipText: "#a0c8a0",
    disclaimerText: "#6a8a6a",
    progCardBg: "#0d3d1a",
    progBannerBg: "#0a2f14",
    progEyebrow: "#4ade80",
    liftRed: "#e74c3c",
    nutriGreen: "#2ecc71",
    actionPrimary: "#f97316",
    backdrop: "rgba(0,0,0,0.75)",
  },
};

// Mutable singleton — populated with Balanced defaults so module-level
// subcomponents have correct values before App() renders for the first time.
export const T = Object.assign({}, THEMES.Balanced);

// Persona header background — used by AppHeader and active workout screen.
export function getHeaderBg(p) {
  const map = { motivational: '#2c3e2c', balanced: '#0d4a63', critical: '#1c1c1e' };
  return map[(p || '').toLowerCase()] || '#0d4a63';
}
